import type { MeetingAudioSelection } from './meeting-audio-input';
import type { AudioInput } from '@/speech/capture/audio-inputs';

export type MeetingAudioSource = 'room' | 'tab' | 'both';

export type MeetingAudioPreferences = {
  source: MeetingAudioSource;
  selection: MeetingAudioSelection;
};

const KEY = 'poneva.meeting.audio-preferences.v1';
const SOURCES = new Set<MeetingAudioSource>(['room', 'tab', 'both']);

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Preferences are convenience only; capture still validates the chosen IDs. */
export function readMeetingAudioPreferences(): MeetingAudioPreferences | null {
  try {
    const value = storage()?.getItem(KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<MeetingAudioPreferences>;
    const selection = parsed.selection;
    if (
      !selection ||
      typeof selection.systemDeviceId !== 'string' ||
      typeof selection.microphoneDeviceId !== 'string' ||
      !SOURCES.has(parsed.source as MeetingAudioSource)
    )
      return null;
    return { source: parsed.source as MeetingAudioSource, selection };
  } catch {
    return null;
  }
}

/** Call this only after a start or resume has successfully opened capture. */
export function rememberMeetingAudioPreferences(value: MeetingAudioPreferences): void {
  try {
    storage()?.setItem(KEY, JSON.stringify(value));
  } catch {
    // Private browsing and disabled storage must not block recording.
  }
}

export function forgetMeetingAudioPreferences(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // Best effort only.
  }
}

export const meetingAudioPreferencesStorageKey = KEY;

/** Reapply only IDs that still exist; an absent monitor is never replaced. */
export function revalidateAudioSelection(
  selection: MeetingAudioSelection,
  inputs: AudioInput[],
): MeetingAudioSelection {
  const available = new Set(inputs.map((input) => input.deviceId));
  return {
    systemDeviceId:
      selection.systemDeviceId && available.has(selection.systemDeviceId)
        ? selection.systemDeviceId
        : '',
    microphoneDeviceId:
      selection.microphoneDeviceId && available.has(selection.microphoneDeviceId)
        ? selection.microphoneDeviceId
        : '',
  };
}
